// Deep links of the signed-in dashboards (provider first).
//
// One convention, shared by every future entry point (a link typed in Safari or
// Chrome, a home-screen shortcut, a push notification, the notification bell):
//
//   /dashboard?tab=<tab>            a tab of the provider space
//   /dashboard?booking=TM-…         the detail of one booking
//   /dashboard?chat=TM-…            the conversation of one booking
//   /dashboard?scooter=srv_…        the scooter workspace (its calendar, prices…)
//   /dashboard?listing=srv_…        a listing, located in the "Annonces" tab
//
// `editService`, `service`, `settlement`, `review` keep their existing meaning.
//
// History rules (the point of this module):
//  - A window (booking detail, chat, scooter workspace) is part of the URL.
//    Opening it from inside the app PUSHES one history entry; closing it goes
//    back, so « Retour » closes the window and the history never fills up with
//    duplicates. Opened from a link (nothing to go back to), closing REPLACES
//    the URL without the parameter.
//  - Tabs are written to the URL with REPLACE: a tab is a view, not a step.
//  - Parameters are merged, never overwritten: opening a booking from the
//    scooter workspace keeps `scooter` in the URL, so closing it restores the
//    workspace.
//  - Tokens never travel in a URL: only resource identifiers do, and the server
//    still decides who may see what.
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const STATE_KEY = 'ridaloPushed';
const KEEP_TAB_KEY = 'ridaloKeepTab';

/** Tabs of the provider space that a link may select. */
export const PROVIDER_TABS = [
  'overview', 'reservations', 'trips', 'annonces', 'messages', 'calendar',
  'customers', 'finances', 'analytics', 'reviews', 'documents', 'payments',
];
export const DEFAULT_PROVIDER_TAB = 'overview';

/** The tab, if `value` is one a link may select. */
export const validProviderTab = (value) => (PROVIDER_TABS.includes(value) ? value : null);

/**
 * Tab a link implies when it names no tab: a booking → reservations, a
 * conversation → messages, a scooter → calendar, a listing → annonces.
 */
export const impliedProviderTab = (params) => {
  if (params.get('chat')) return 'messages';
  if (params.get('booking')) return 'reservations';
  if (params.get('scooter')) return 'calendar';
  if (params.get('listing')) return 'annonces';
  return null;
};

/** Identifier shapes accepted in the URL (anything else is ignored). */
const ID_PATTERNS = {
  booking: /^TM-[A-Za-z0-9-]{3,40}$/,
  chat: /^TM-[A-Za-z0-9-]{3,40}$/,
  scooter: /^[A-Za-z0-9_-]{3,64}$/,
  listing: /^[A-Za-z0-9_-]{3,64}$/,
};
export const cleanId = (name, value) => {
  if (typeof value !== 'string') return null;
  const re = ID_PATTERNS[name];
  return re && re.test(value) ? value : null;
};

/** New query string: `patch` entries set (or removed when null / empty). */
export const mergeParams = (search, patch) => {
  const next = new URLSearchParams(search);
  Object.entries(patch).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') next.delete(k);
    else next.set(k, v);
  });
  return next;
};

/** A dashboard URL, for the code that builds links (bell, future push payloads). */
export const dashboardUrl = (params = {}) => {
  const q = mergeParams('', params).toString();
  return q ? `/dashboard?${q}` : '/dashboard';
};

/**
 * Navigation state for a link followed from a screen that already shows the
 * dashboard (the notification bell): the window it opens then counts as pushed
 * from the app, so closing it / « Retour » goes back one step instead of leaving a
 * duplicate entry. Elsewhere (landing page, e-mail…) it returns undefined: the
 * link is simply a first entry and closing replaces the URL.
 */
export const stateForDashboardLink = (target, currentPathname) => {
  if (currentPathname !== '/dashboard') return undefined;
  let query;
  try { query = new URL(target, 'https://ridalo.invalid').searchParams; } catch { return undefined; }
  const names = ['booking', 'chat', 'scooter'].filter((n) => cleanId(n, query.get(n)));
  return names.length ? { [STATE_KEY]: names } : undefined;
};

/**
 * A window driven by one URL parameter.
 *   value  the identifier in the URL (or null)
 *   open   (id, extra?) push an entry with the parameter (merged with the rest)
 *   close  go back if this window was opened from inside the app, else replace
 *   swap   (id) change the identifier without a new history entry
 */
export const useUrlOverlay = (name) => {
  const location = useLocation();
  const navigate = useNavigate();
  const value = cleanId(name, new URLSearchParams(location.search).get(name));
  const pushedHere = Array.isArray(location.state?.[STATE_KEY]) && location.state[STATE_KEY].includes(name);

  const open = useCallback((id, extra = {}) => {
    if (!cleanId(name, id) || id === value) return;
    navigate(
      { pathname: location.pathname, search: `?${mergeParams(location.search, { [name]: id, ...extra })}` },
      { state: { [STATE_KEY]: [name], [KEEP_TAB_KEY]: true } },
    );
  }, [navigate, location.pathname, location.search, name, value]);

  const close = useCallback(() => {
    if (pushedHere) {
      navigate(-1);
    } else {
      navigate(
        { pathname: location.pathname, search: `?${mergeParams(location.search, { [name]: null })}` },
        { replace: true, state: location.state },
      );
    }
  }, [navigate, pushedHere, location.pathname, location.search, location.state, name]);

  const swap = useCallback((id) => {
    if (!cleanId(name, id)) return;
    navigate(
      { pathname: location.pathname, search: `?${mergeParams(location.search, { [name]: id })}` },
      { replace: true, state: location.state },
    );
  }, [navigate, location.pathname, location.search, location.state, name]);

  return { value, open, close, swap, pushedHere };
};

/**
 * The provider tab, taken from the URL (single source of truth).
 * Returns [tab, setTab]; setTab replaces the URL (never adds a history entry).
 * A link without `tab` that names a booking / conversation / scooter / listing
 * is completed once with the implied tab (replace), so closing the window leaves
 * the page behind it unchanged.
 */
export const useProviderTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tab = validProviderTab(params.get('tab')) || DEFAULT_PROVIDER_TAB;
  // Opened from a screen of the app (the tab behind is already the right one): keep it.
  const keepTab = !!location.state?.[KEEP_TAB_KEY];

  const setTab = useCallback((next) => {
    const wanted = validProviderTab(next) || DEFAULT_PROVIDER_TAB;
    navigate(
      { pathname: location.pathname, search: `?${mergeParams(location.search, { tab: wanted === DEFAULT_PROVIDER_TAB ? null : wanted, listing: null })}` },
      { replace: true, state: location.state },
    );
  }, [navigate, location.pathname, location.search, location.state]);

  useEffect(() => {
    if (keepTab || validProviderTab(params.get('tab'))) return;
    const implied = impliedProviderTab(params);
    if (implied) {
      navigate(
        { pathname: location.pathname, search: `?${mergeParams(location.search, { tab: implied })}` },
        { replace: true, state: location.state },
      );
    }
    // Only when the URL changes: the tab is completed once per arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return [tab, setTab];
};
